import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import ErrorMessage from 'components/ErrorMessage';

const StartAIInterview = ({ setJobRequest, setPage, allRequest, currentItem, jobApply }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [templates, setTemplates] = useState([]);
  const [templateID, setTemplateID] = useState('');
  const [templateError, setTemplateError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interviewLink, setInterviewLink] = useState('');
  
  // ✅ NEW STATE: Scheduled Date & Time
  const [scheduledAt, setScheduledAt] = useState('');

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await apiGet(apiPath.aiInterviewTemplateList, {});
        setTemplates(res?.data?.results || []);
      } catch (error) {
        console.log('error loading templates:', error);
      }
    };
    loadTemplates();

    // ✅ FIX: Get local time and format it for datetime-local input (YYYY-MM-DDTHH:mm)
    const now = new Date();
    // now.setHours(now.getHours() + 1); // Add 1 hour as you originally intended

    // Get local date parts
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    // Combine into the format the input expects (local time, not UTC)
    const localDateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
    
    setScheduledAt(localDateTimeString);
  }, []);

  const handleGenerate = async () => {
    if (!templateID) {
      setTemplateError(true);
      return;
    }
    
    // ✅ Optional: Validate that scheduledAt is selected
    if (!scheduledAt) {
      notification.error('Please select a scheduled date and time.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiPost(apiPath.startAIInterview, {
        jobRequestID: currentItem._id,
        templateID,
        scheduledAt: new Date(scheduledAt).toISOString(), // ✅ Send the scheduled time
      });
      if (res?.data?.success) {
        setInterviewLink(res.data.results.interviewLink);
        notification.success('AI interview link generated and sent to the candidate.');
        allRequest();
      } else {
        notification.error(res?.data?.message || 'Failed to generate AI interview.');
      }
    } catch (error) {
      console.log('error:', error);
      notification.error('Failed to generate AI interview.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setJobRequest(false);
  };

  return (
    <>
      <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
        <div className='relative w-auto my-6 mx-auto max-w-3xl'>
          <div className='sm:py-4 sm:px-2 py-8 px-7'>
            <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[500px]'>
              <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
                <h3 className='text-xl font-semibold'>{t('INTERVIEW') || 'AI Interview'}</h3>
                <button
                  className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                  onClick={handleClose}
                  type='button'
                >
                  <span className='text-[#B8BBBF] text-4xl' title='Close'>×</span>
                </button>
              </div>

              <div className='relative p-6 flex-auto'>
                <div className='grid sm:grid-cols-1 gap-4'>
                  {/* Template Selector */}
                  <div className='w-full'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Interview Template<span className='text-red-500'>*</span>
                    </label>
                    <select
                      className='block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                      value={templateID}
                      onChange={(e) => {
                        setTemplateID(e.target.value);
                        setTemplateError(false);
                      }}
                    >
                      <option value=''>Select a template</option>
                      {templates.map((tpl) => (
                        <option key={tpl._id} value={tpl._id}>
                          {tpl.role} · {tpl.difficulty} · {tpl.totalQuestions} questions
                        </option>
                      ))}
                    </select>
                    {templateError && <ErrorMessage message='Please select an interview template.' />}
                  </div>

                  {/* ✅ NEW: Scheduled Date & Time Picker */}
                  <div className='w-full'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Scheduled Date & Time<span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='datetime-local'
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className='block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                    />
                    <p className='text-xs text-gray-500 mt-1'>
                      Candidate will be able to start the interview 15 minutes before and up to 15 minutes after this time.
                    </p>
                  </div>

                  {interviewLink && (
                    <div className='w-full'>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Candidate Link
                      </label>
                      <input
                        type='text'
                        readOnly
                        value={interviewLink}
                        className='block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] bg-gray-100 focus:outline-none focus:ring-0'
                      />
                      <p className='text-xs text-gray-500 mt-1'>
                        This link has already been emailed to the candidate and expires in 7 days.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b gap-4'>
                {!interviewLink && (
                  <button
                    className='bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 disabled:opacity-50'
                    type='button'
                    onClick={handleGenerate}
                    disabled={isSubmitting || !scheduledAt} // ✅ Disable if no date selected
                  >
                    {isSubmitting ? 'Generating…' : 'Generate AI Interview'}
                  </button>
                )}

                <button
                  className='bg-gray-200 text-gray-700 active:bg-gray-300 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150'
                  type='button'
                  onClick={handleClose}
                >
                  {interviewLink ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='opacity-25 fixed inset-0 z-40 bg-black' />
    </>
  );
};

export default StartAIInterview;